/**
 * Parse a JSON string that comes from a custom-element `@Input` attribute.
 * Returns the parsed value on success, the `fallback` (default `null`) on
 * empty/missing input, and the `fallback` plus a console warning on parse
 * failure.
 *
 * Many CE-bridge components used to do `try { JSON.parse(value); } catch {}`
 * which silently swallows malformed host JSON and leaves the form blank with
 * no clue why. Use this helper so the failure shows up in DevTools at least.
 *
 * Example:
 *   @Input()
 *   set rowDataStr(value: string) {
 *     this.rowData = safeJsonInput(value, {}, 'rowDataStr');
 *   }
 */
export function safeJsonInput<T = any>(
  value: unknown,
  fallback: T = null as unknown as T,
  contextLabel = 'CE @Input',
): T {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  if (typeof value !== 'string') {
    return value as unknown as T;
  }
  try {
    return JSON.parse(value) as T;
  } catch (err) {
    console.warn(
      `[safeJsonInput] Failed to parse ${contextLabel} — falling back to default.`,
      { value, error: err },
    );
    return fallback;
  }
}
